import DOMPurify from 'dompurify';
import * as pagesApi from '@/api/artist/pages';

function defaultWallPaperUrl() {
  return 'https://static.moele.me/img/member/artist-page-bg.jpg';
}

const state = {
  initial: true,
  loading: true,
  favourites: [],
  selectedFavourites: [],
  data: {
    id: null,
    avatar: {
      url: '',
    },
    object: null,
    username: null,
    country: null,
    hero_level: 0,
    description: '',
    homepage: null,
    showreel: null,
    sns: [],
    tools: [],
    html: {
      url: null,
      title: null,
      description: null,
    },
    picks: [],
    banners: [],
    elements: [],
    collections: [],
  },
};

const getters = {
  isLoading: state => state.loading,
  get: ({ data }) => key => (data[key]),
  getUrl: ({ data }) => key => (data[key] ? data[key].url : ''),
  getPageData: state => state.data,
  getTotalElementCount: ({ data }) => {
    if (data.elements.length) {
      return data.elements
        .map(element => element.total_count)
        .reduce((x, y) => x + y);
    }
    return 0;
  },
  getTotalElementMediaType: ({ data: { elements } }) => (elements.length ? elements.map(element => element.media_type) : elements),
  getArtistAvatarPreview: ({ data: { avatar } }) => {
    const defaultAvatar = 'https://www.gravatar.com/avatar/b600dca44add74264d7af211290a423d?d=https%3A%2F%2Fstatic.motionelements.com%2Fimg%2Fmember%2Fartist-01.jpg&s=80';
    return !avatar.url ? defaultAvatar : avatar.url;
  },
  getArtistAvatar: ({ data: { avatar } }) => avatar.url,
  getArtistBannerPreview: ({ data }) => type => {
    if (data.banners.length) {
      const banner = data.banners.find(banner => banner.type === type);
      return banner ? banner.image.url : defaultWallPaperUrl();
    }
    return defaultWallPaperUrl();
  },
  getArtistBanner: ({ data }) => type => {
    if (data.banners.length) {
      const banner = data.banners.find(banner => banner.type === type);
      return banner ? banner : null;
    }
    return null;
  },
  hasArtistBannerPromo: ({ data }) => {
    if (data) {
      const banner = data.banners.filter(banner => banner.type === 'promo' && banner.image.url);
      if (banner.length > 0) return true;
    }
    return false;
  },
  getPageUpdatePayload: state => ({
    description: state.data.description,
    homepage_url: getters.getUrl(state)('homepage'),
    showreel_url: getters.getUrl(state)('showreel'),
    tools: state.data.tools.map(tool => _.get(tool, 'id') || tool),
    picks: getters.getSelectedFavouritesIds(state),
    sns: {
      youtube: getters.getSnsUsername(state)('youtube'),
      vimeo: getters.getSnsUsername(state)('vimeo'),
      facebook: getters.getSnsUsername(state)('facebook'),
      twitter: getters.getSnsUsername(state)('twitter'),
    },
  }),
  getSnsUsername: ({ data: { sns } }) => type => {
    const regExp = new RegExp(`^https?://(www.)?${type}.com/(.*)`);
    const snsItem = sns.find(item => item.type === type);
    if (snsItem) {
      return snsItem.url.match(regExp) ? snsItem.url.match(regExp)[2] : snsItem.url;
    }
  },
  getSnsUrl: ({ data: { sns } }) => type => {
    const snsItem = sns.find(item => item.type === type);
    if (snsItem) {
      return _.get(snsItem, 'url');
    }
  },
  getFavourites: state => state.favourites,
  getSelectedFavourites: ({ selectedFavourites }) => selectedFavourites,
  getSelectedFavouritesIds: state => getters.getSelectedFavourites(state).map(favourite => favourite.id),
  //
  getAdditionalInfoPayload: state => ({
    homepage_url: getters.getUrl(state)('homepage'),
    showreel_url: getters.getUrl(state)('showreel'),
    tools: state.data.tools.map(tool => _.get(tool, 'id') || tool),
    sns: {
      youtube: getters.getSnsUsername(state)('youtube'),
      vimeo: getters.getSnsUsername(state)('vimeo'),
      facebook: getters.getSnsUsername(state)('facebook'),
      twitter: getters.getSnsUsername(state)('twitter'),
    },
  }),
};

const actions = {
  getPage({ commit }) {
    commit('setLoading', true);
    return pagesApi.getProfilePage()
      .then(({ data: { data } }) => {
        commit('setPage', data);
        commit('setLoading', false);
      });
  },
  updatePageDetail({ commit }, payload) {
    return pagesApi.updateProfilePage(payload)
      .then(({ data: { data } }) => {
        console.log(data);
        commit('setPage', data);
      });
  },
  updatePage({ commit, getters }) {
    return pagesApi.updateProfilePage(getters.getPageUpdatePayload)
      .then(({ data: { data } }) => {
        commit('setPage', data);
        commit('setLoading', false);
      });
  },
  updateFavourites({ commit, getters }) {
    commit('setLoading', true);
    return pagesApi.updateProfilePage({ picks: getters.getSelectedFavouritesIds })
      .then(({ data: { data } }) => {
        // commit('setPage', data);
        commit('set', { key: 'picks', value: data.picks });
        commit('setLoading', false);
      });
  },
  getFavourites({ commit }) {
    return pagesApi.getFavourites()
      .then(({ data: { data } }) => {
        commit('setFavourites', data);
      });
  },
  //
  saveDescription({ state }) {
    return pagesApi.updateProfilePage({
      description: state.data.description,
    })
      .catch((err) => {
        console.error(err);
      });
  },
  saveAdditionalInfo({ getters }) {
    return pagesApi.updateProfilePage(getters.getAdditionalInfoPayload)
      .catch(err => {
        console.log(err);
      });
  },
  removeCoverImage({ commit }) {
    return pagesApi.updateProfilePage({ cover: false })
      .then((response) => {
        console.log(response);
        commit('setPage', response.data.data);
      })
      .catch(err => {
        console.error(err);
      });
  },
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },
  setPage(state, artistData) {
    state.data = artistData;
  },
  uploadAvatar(state, avatarUrl) {
    if (state.data.avatar) {
      state.data.avatar.url = avatarUrl;
    } else {
      state.data.avatar = { url: avatarUrl };
    }
  },
  set(state, { key, value }) {
    state.data[key] = value;
  },
  setDescription(state, html) {
    state.data.description = DOMPurify.sanitize(html);
  },
  setUrl(state, { key, value }) {
    if (state.data[key]) {
      state.data[key].url = value;
    } else {
      state.data[key] = { url: value };
    }
  },
  // state.data.tools
  addTool(state, tool) {
    state.data.tools.push(tool);
  },
  removeTool(state, tool) {
    const index = _.has(tool, 'id')
      ? state.data.tools.findIndex(x => _.get(x, 'id') === tool.id)
      : state.data.tools.findIndex(x => x === tool);

    if (index > -1) {
      state.data.tools.splice(index, 1);
    }
  },
  //
  setSNS({ data: { sns } }, { type, url }) {
    const targetSns = sns.find(item => item.type === type);
    if (targetSns) {
      targetSns.url = url;
    } else {
      sns.push({ type, url });
    }
  },
  setFavourites(state, favourites) {
    state.favourites = favourites;
    state.data.picks.forEach(pick => {
      state.selectedFavourites.push(favourites.find(favourite => favourite.name === pick.heading));
    });
  },
  addFavourite(state, favourite) {
    state.selectedFavourites.push(favourite);
  },
  removeFavourite({ selectedFavourites }, index) {
    selectedFavourites.splice(index, 1);
  },
  //
  setCoverImage(state, banner) {
    const index = state.data.banners.findIndex(x => x.type === banner.type);
    if (index > -1) state.data.banners.splice(index, 1);
    state.data.banners.push(banner);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
